import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Use relative URLs in production (same-origin, no CORS issues)
// Falls back to REACT_APP_BACKEND_URL for development
const API_URL = (() => {
    const envUrl = process.env.REACT_APP_BACKEND_URL || '';
    // If the env URL matches current origin, use empty string (relative)
    if (typeof window !== 'undefined' && envUrl && new URL(envUrl).origin === window.location.origin) {
        return '';
    }
    return envUrl;
})();

// Configure axios defaults
axios.defaults.withCredentials = true;

export const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
            setUser(data);
        } catch (error) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        try {
            const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password }, { withCredentials: true });
            setUser(data);
            return { success: true };
        } catch (error) {
            return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
        }
    };

    const register = async (email, password, name, role = 'employee') => {
        try {
            const { data } = await axios.post(`${API_URL}/api/auth/register`, { email, password, name, role }, { withCredentials: true });
            setUser(data);
            return { success: true };
        } catch (error) {
            return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) || error.message };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(false);
        }
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        checkAuth,
        isAuthenticated: !!user && user !== false,
        isAdmin: user?.role === 'admin',
        isHRManager: user?.role === 'hr_manager',
        isEmployee: user?.role === 'employee',
        canManage: ['admin', 'hr_manager'].includes(user?.role),
        canEditRevenue: ['admin', 'hr_manager', 'manager', 'operations_manager', 'operations_executive', 'employee'].includes(user?.role),
        canManageOps: ['admin', 'hr_manager', 'manager', 'operations_manager', 'operations_executive'].includes(user?.role)
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
